import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material";
import { logMsg } from "../../common/Logger";
import ReleaseNotesClient from "../../clients/ReleaseNotesClient";
import { trackPageLoadEvent } from "../../shared/services/analytics/AnalyticsService";

const PageHeader = ({ title, subtitle }) => {
  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h4">{title}</Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Typography variant="body1">{subtitle}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

const Hero = ({ title, subtitle }) => {
  return (
    <Box
      style={{ backgroundColor: "whitesmoke" }}
      px={[4, 4, 4, 14, 14]}
      py={3}
      mb={3}
    >
      <PageHeader title={title} subtitle={subtitle} />
    </Box>
  );
};

Hero.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

const Notes = ({ notes }) => {
  return (
    <Box px={[4, 4, 4, 14, 14]} py={3}>
      {notes.map((note, noteIndex) => {
        const noteKey = note.id || note.date || note.text[0];

        return (
          <Box
            key={noteKey}
            mb={6}
            sx={{
              borderBottom: "1px solid #f2f2f2",
              paddingBottom: "16px",
            }}
          >
            <Typography data-testid={`notes_title_${noteIndex}`} variant="h6">
              {note.text[0]}
            </Typography>

            <ul>
              {note.text.slice(1).map((item) => (
                <li key={`${noteKey}-${item}`}>
                  <Typography variant="body1">{item}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        );
      })}
    </Box>
  );
};

Notes.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      date: PropTypes.string,
      text: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
};

const ReleaseNotes = (props) => {
  const { t, hideOrShow, user } = props;

  const { pathname } = useLocation();
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getReleaseNotes();
  }, [pathname]);

  useEffect(() => {
    trackPageLoadEvent("fv:emp:release note", user);
  }, [user]);

  const getReleaseNotes = async () => {
    hideOrShow(true);
    await ReleaseNotesClient.getReleaseNotes()
      .then((response) => {
        response !== null &&
          response.sort((a, b) => new Date(b.date) - new Date(a.date));

        setNotes(response);
      })
      .catch(() => {
        logMsg("Error fetching release notes.");
      })
      .finally(() => {
        hideOrShow(false);
      });
  };

  return (
    notes && (
      <section>
        <Hero
          title={t("ReleaseNotes.title")}
          subtitle={t("ReleaseNotes.subtitle")}
        />
        <Grid container>
          <Grid item xs={12} sm={12} md={10}>
            <Notes notes={notes} />
          </Grid>
        </Grid>
      </section>
    )
  );
};

ReleaseNotes.propTypes = {
  hideOrShow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({}).isRequired,
};

export default withTranslation("emp")(ReleaseNotes);
